import type { PurchaseOption, PurchaseRoute } from '../../utils/purchase'
import { availabilityCaption } from '../../utils/purchase'
import { pkr } from '../../utils/format'

/* How the visitor is buying: on the spot, or on booking.

   The car is one price and one monthly figure whichever is chosen — what
   changes is the payment made up front, so the two segments sit directly
   above the figure they change and nothing else on the surface moves.

   A radio group rather than two buttons: it is one exclusive choice out of
   two, and that is what a screen reader should hear. The roving-tabindex the
   pattern asks for is left out on purpose — with two options the arrow keys
   have nowhere to go that Tab does not already reach.

   On a car the showroom offers one way only there is no choice to make, and a
   control with a single segment would be a lie about that: the segments are
   replaced by a caption saying which way it can be had. Everything under them
   is the same either way — the figure asked up front, and the line explaining
   what it buys — because that is a fact about the route, not about being able
   to pick it. The caption is sized to the control it stands in for, so a grid
   of cards holds its rows whichever kind of car is in it. */

type PurchaseRoutePickerProps = {
  options: PurchaseOption[]
  value: PurchaseRoute
  onChange: (route: PurchaseRoute) => void
  /** The car being bought, for the group's accessible name. */
  name: string
  /** 'sm' on a listing card, 'lg' in the detail page's deal panel. */
  size?: 'sm' | 'lg'
  className?: string
}

function PurchaseRoutePicker({
  options,
  value,
  onChange,
  name,
  size = 'sm',
  className = '',
}: PurchaseRoutePickerProps) {
  const large = size === 'lg'

  /* The held route, unless this car is not offered on it — a card keeps its
     own choice while a visitor scrolls past twenty of them, and the twenty-
     first may be bookable only. */
  const selected = options.find((option) => option.route === value) ?? options[0]
  if (!selected) return null

  const caption = availabilityCaption(options)

  return (
    <div className={className}>
      {caption ? (
        /* Not a control and not a status: a plain line of type saying what
           this car is. The lime is the same the segments use when chosen,
           because it is answering the same question. */
        <p
          className={`flex items-center justify-center rounded-full border border-lime/25 bg-lime/[0.07] font-semibold tracking-[-0.01em] text-lime ${
            large ? 'h-11 text-[13px]' : 'h-9 text-[11.5px]'
          }`}
        >
          {caption}
        </p>
      ) : (
        <div
          role="radiogroup"
          aria-label={`How you are buying the ${name}`}
          className="grid grid-cols-2 gap-1 rounded-full border border-line bg-surface/70 p-1"
        >
          {options.map((option) => {
            const current = option.route === selected.route

            return (
              <button
                key={option.route}
                type="button"
                role="radio"
                aria-checked={current}
                onClick={() => onChange(option.route)}
                className={`rounded-full font-semibold tracking-[-0.01em] transition duration-200 ${
                  large ? 'h-9 text-[13px]' : 'h-7 text-[11.5px]'
                } ${
                  current
                    ? 'bg-lime text-void shadow-action'
                    : 'text-steel hover:bg-raised hover:text-platinum'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      )}

      {/* What the chosen route asks for. Every car quotes a figure here —
          the showroom's own where it typed one, the house's standard share
          of the price otherwise (see purchaseOptionsFor) — so the row is
          always present and always answers. It used to disappear on a car
          nobody had typed a payment for, which made the card shorter on some
          cars than on others, and shorter again when a paint or a route was
          picked: a grid that jumped under the cursor that picked it.

          The one car with nothing to print is one with no price to work from,
          and the price above it is saying "on request" for the same reason.

          The figure is announced when it changes rather than the segments
          being described by it, so the two amounts are never both read out
          at once. */}
      <div aria-live="polite">
        <div
          className={`flex items-baseline justify-between gap-3 ${large ? 'mt-3.5' : 'mt-2'}`}
        >
          <span className={`text-dim ${large ? 'label-sm' : 'text-[11.5px]'}`}>
            Down payment
          </span>

          {selected.downPayment === null ? (
            <span
              className={`shrink-0 font-medium text-lime-deep ${large ? 'text-[13px]' : 'text-[12px]'}`}
            >
              On request
            </span>
          ) : (
            <span className="flex shrink-0 items-baseline gap-1">
              <span className={`font-medium text-dim ${large ? 'text-[11px]' : 'text-[10.5px]'}`}>
                PKR
              </span>
              <strong
                className={`font-display leading-none font-semibold tracking-[-0.015em] text-platinum tabular-nums ${
                  large ? 'text-[20px]' : 'text-[14px]'
                }`}
              >
                {pkr.format(selected.downPayment)}
              </strong>
            </span>
          )}
        </div>
      </div>

      {/* On the car's own page there is room to say where the figure came
          from. A payment the showroom has not typed for this car is the
          house's standard terms, and the page says so rather than passing
          arithmetic off as a quote. The card has no room for that line and
          does not need it: the desk confirms every deal anyway. */}
      {large ? (
        <p className="mt-2 text-[12.5px] leading-relaxed text-steel">
          {selected.note}
          {selected.standard
            ? ' Quoted on our standard terms — the desk confirms the figure on this car.'
            : ''}
        </p>
      ) : null}
    </div>
  )
}

export default PurchaseRoutePicker
